import PreferencesRequest from "@/utils/constants/PreferencesRequest.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import StringConstants from "@/utils/constants/StringConstants.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import PreferencesCache from "@/utils/cache/PreferencesCache.ts";
import NaMaMetaData = namaTypes.NaMaMetaData;
import FieldMetaData = namaTypes.FieldMetaData;
import BasicFieldMetaData = namaTypes.BasicFieldMetaData;

export default class MetaData {
  static async fetchPreferences(request: Record<any, any>, callback: Function) {
    let preferences = ClientCache.uiPreferencesCache[request.owner.ownerId];
    if (!preferences) {
      preferences = {};
      ClientCache.uiPreferencesCache[request.owner.ownerId] = preferences;
    }

    // Check persistent cache (IndexedDB) for cacheable properties
    const { cached, uncachedProperties } = await PreferencesCache.getCachedPreferences(
      request.owner.ownerId,
      request.properties,
      request.editLayoutScreenName,
      request.listLayoutScreenName,
      request.searcherLayoutScreenName
    );

    // Apply cached values to preferences
    for (const property of Object.keys(cached)) {
      preferences[property] = cached[property];
    }

    // Filter to only fetch properties not in persistent cache and not already in memory
    let diffProperties = this.getUnfetchedProperties(preferences, { ...request, properties: uncachedProperties });

    if (diffProperties.length) {
      request.properties = diffProperties;
      let params: NamaAxiosRequestConfig = {
        params: {
          inBody: true
        },
        data: request,
        noLoadingIndicator: true,
      };
      let newPreferences = await NamaCommon.namaFetch(NamaURLS.FETCH_PREFERENCES, params);
      for (let property of diffProperties)
        preferences[property] = newPreferences[property];

      // Cache the newly fetched preferences in IndexedDB
      await PreferencesCache.cachePreferences(
        request.owner.ownerId,
        newPreferences,
        request.editLayoutScreenName,
        request.listLayoutScreenName,
        request.searcherLayoutScreenName
      );
    }
    return callback(preferences);
  }

  static getUnfetchedProperties(current: Record<any, any>, request: Record<any, any>) {
    if (!current)
      return request.properties;
    let unFetchedProperties: PreferencesRequest[] = [];
    for (let property of request.properties)
      if (!this.isPropertyFetched(current, property, request))
        unFetchedProperties.push(property);
    return unFetchedProperties;
  }

  static isPropertyFetched(currentPreferences: Record<any, any>, property: PreferencesRequest, request: Record<any, any>) {
    if (ObjectChecker.areEqual(property, PreferencesRequest.EDIT_LAYOUT))
      return ObjectChecker.isNotEmptyOrNull(currentPreferences.editLayout)
        && this.isTheSameLayout(currentPreferences.editLayout.id, request.editLayoutScreenName);
    if (ObjectChecker.areEqual(property, PreferencesRequest.LIST_LAYOUT))
      return ObjectChecker.isNotEmptyOrNull(currentPreferences.listLayout)
        && this.isTheSameLayout(currentPreferences.listLayout.id, request.listLayoutScreenName);
    if (ObjectChecker.areEqual(property, PreferencesRequest.LIST_LAYOUT_PREFERENCES))
      return ObjectChecker.isNotEmptyOrNull(currentPreferences.listLayoutPreferences)
        && this.isTheSameLayout(currentPreferences.listLayoutPreferences.layoutId, request.listLayoutScreenName);
    if (ObjectChecker.areEqual(property, PreferencesRequest.VIEW_NAMES))
      return ObjectChecker.isNotEmptyOrNull(currentPreferences.viewNames);
    if (ObjectChecker.areEqual(property, PreferencesRequest.META_DATA))
      return ObjectChecker.isNotEmptyOrNull(currentPreferences.metaData);
    if (ObjectChecker.areEqual(property, PreferencesRequest.SEARCHER_LAYOUT))
      return ObjectChecker.isNotEmptyOrNull(currentPreferences.searcherLayout)
        && this.isTheSameLayout(currentPreferences.searcherLayout.id, request.searcherLayoutScreenName);
    return false;
  }

  static isTheSameLayout(actualId: string | null | undefined, toCheckId: string | null | undefined) {
    if (ObjectChecker.isEmptyOrNull(actualId))
      actualId = StringConstants.DEFAULT;
    if (ObjectChecker.isEmptyOrNull(toCheckId))
      toCheckId = StringConstants.DEFAULT;
    return ObjectChecker.areEqual(actualId, toCheckId);
  }

  static getCachedDisplayedColumns(entityType, listViewName): string[] | null | undefined {
    let cachedPreferences= ClientCache.uiPreferencesCache[entityType];
    let cachedListLayoutPreferences = cachedPreferences ? cachedPreferences.listLayoutPreferences : null;
    if (cachedListLayoutPreferences != null && this.isTheSameLayout(cachedListLayoutPreferences.layoutId, listViewName))
      return cachedListLayoutPreferences.displayedColumns;
    return null;
  }

  static findFieldMetadata(fieldId: string, metaData: namaTypes.NaMaMetaData): FieldMetaData {
    let fmd = this._findFieldMetadata(fieldId, metaData);
    if (!fmd && (fieldId.endsWith(".code") || fieldId.endsWith(".name"))) {
      let pfmd = this._findFieldMetadata(fieldId.substring(0, fieldId.lastIndexOf(".")), metaData)
      if (pfmd && (pfmd.fieldType === FieldType.Reference || pfmd.fieldType === FieldType.Genericreference)) {
        let fieldMetaData = NamaCommon.cloneObject(pfmd);
        fieldMetaData['pseudoRefSubField'] = StringUtils.substringAfterLast(fieldId, ".");
        return fieldMetaData;
      }
    }
    return fmd;
  }

  private static _findFieldMetadata(fieldId: string, metaData: namaTypes.NaMaMetaData): FieldMetaData {
    this.makeFieldSearchFast(metaData)
    let md = metaData.fieldsMetaData ?? [];
    return md[metaData['fieldsIndexMap'][fieldId]];
  }

  static makeFieldSearchFast(metaData: namaTypes.NaMaMetaData) {
    if (metaData['fieldsIndexMap'])
      return;
    metaData['fieldsIndexMap'] = {};
    for (let i = 0; i < (metaData.fieldsMetaData ?? []).length; i++) {
      metaData['fieldsIndexMap'][metaData.fieldsMetaData![i].fieldId] = i;
    }
  }

  static getFieldParentDetail(metadata: NaMaMetaData, fieldId: string | undefined) {
    return this.getFieldParentDetail0(metadata, fieldId, true);
  }

  static getFieldParentDetail0(metadata: NaMaMetaData, fieldId: string | undefined, firstCall: boolean): string | undefined {
    if (!metadata['detailsMetaData'])
      this.initDetailsMetaData(metadata);
    if (!fieldId)
      return undefined;
    let fieldMetadata: FieldMetaData | undefined;
    if (firstCall)
      fieldMetadata = this.getFieldMetadata(metadata, fieldId);
    else
      fieldMetadata = this.getDetailFieldMetadata(metadata, fieldId);
    if (fieldMetadata && !firstCall)
      return fieldId;
    let lastDotIndex = fieldId.lastIndexOf('.');
    if (lastDotIndex < 0)
      return undefined;
    fieldId = fieldId.substring(0, lastDotIndex);
    return this.getFieldParentDetail0(metadata, fieldId, false);
  }

  static getDetailFieldMetadata(metadata: NaMaMetaData, fieldId: string) {
    let index = this.getFieldIdIndex(fieldId, metadata['detailsMetaData']);
    if (index < 0)
      return undefined;
    return metadata.fieldsMetaData![index];
  }

  static getFieldMetadata(metadata: NaMaMetaData, id: string) {
    let start: number = Date.now();
    let index: number = this.getFieldIdIndex(id, metadata.fieldsMetaData ?? []);
    let end: number = Date.now();
    if (end - start > 3)
      console.log(id + " Takes " + (end - start));
    if (index < 0)
      return undefined;
    return metadata.fieldsMetaData![index];
  }

  static getFieldIdIndex(id: string, toSearchList: FieldMetaData[]) {
    let i = 0;
    for (let fieldMetaData of toSearchList) {
      if (fieldMetaData.id === id) {
        return i;
      }
      i++;
    }
    return -1;
  }

  static initDetailsMetaData(metadata: NaMaMetaData) {
    let detailsMetaData: FieldMetaData[] = [];
    for (let metaData of metadata.fieldsMetaData ?? []) {
      if (metaData.detail)
        detailsMetaData.push(metaData);
    }
    metadata['detailsMetaData'] = detailsMetaData;
  }

  static prepareMetadataFieldsParents(metadata: NaMaMetaData) {
    if (!metadata || metadata['detailFields']) return;
    const detailFields: Map<string, any> = new Map();
    metadata['detailFields'] = detailFields;
    for (const f of metadata.fieldsMetaData ?? [])
      if (f.detail && f.fieldId) detailFields.set(f.fieldId, f);
    for (const f of metadata.fieldsMetaData ?? []) {
      if (f.detail || !f.fieldId) continue;
      let isSubField = false;
      for (const key of detailFields.keys()) {
        if (f.fieldId.startsWith(key + ".")) {
          const detailField = detailFields.get(key);
          const subFields = detailField.subFields || [];
          subFields.push(f);
          detailField.subFields = subFields;
          (f as any).parentDetail = detailField;
          isSubField = true;
          break;
        }
      }
      (f as any).headerField = !isSubField;
    }
  }

  static listAllCalculatedFields(fieldsMetaData: FieldMetaData[] | undefined): string[] {
    let guiFields: string[] = [];
    if (!fieldsMetaData)
      return [];
    for (let metaData of fieldsMetaData) {
      if (metaData.calculated && metaData.fieldId)
        guiFields.push(metaData.fieldId);
    }
    return guiFields;
  }

  static createReferenceFieldMetaData(id: string, referencedEntity: string) {
    return {
      fieldId: id,
      fieldType: FieldType.Reference,
      referencedEntityType: referencedEntity
    } as BasicFieldMetaData;
  }

  static createTextFieldMetaData(id: string) {
    return {
      fieldId: id,
      fieldType: FieldType.Text
    } as BasicFieldMetaData;
  }
}
